"use client";

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  getVehicleById,
  getVehicleIdFromSearchParams,
  getVehicleLabelById,
  getVehicleOptions,
  LOCATION_BOOKING_FORM_ID,
} from "./locationBooking";
import {
  formatEuro,
  getRentalPriceTiers,
  RENTAL_DURATION_OPTIONS,
  type RentalDurationTierKey,
} from "./pricing";
import type { Locale } from "@/config/i18n";
import { VEHICLES_UI, vehiclesBasePath } from "./vehiclesUi";
import { tr } from "@/config/localeText";
import { getAttribution } from "@/lib/attribution-client";

type QuickBookingFormData = {
  name: string;
  email: string;
  phone: string;
  vehicleId: string;
  rentalDurationTier: RentalDurationTierKey;
  date: string;
  message: string;
  honeypot?: string;
};

const splitName = (fullName: string) => {
  const safeName = fullName.trim().replace(/\s+/g, " ");
  const [firstName = "", ...rest] = safeName.split(" ");
  return { firstName, lastName: rest.join(" ") || firstName };
};

export default function QuickBookingForm({
  locale = "fr",
}: {
  locale?: Locale;
}) {
  const ui = tr(VEHICLES_UI, locale).form;
  const durationLabels = tr(VEHICLES_UI, locale).durationLabels;
  const params = useSearchParams();
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const vehicleOptions = useMemo(() => getVehicleOptions(), []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<QuickBookingFormData>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      vehicleId: "",
      rentalDurationTier: "oneToThreeDays",
      date: "",
      message: "",
    },
  });

  useEffect(() => {
    const vehicleFromUrl = getVehicleIdFromSearchParams(params.get("vehicle"));
    if (vehicleFromUrl) {
      setValue("vehicleId", vehicleFromUrl, { shouldDirty: false });
    }
  }, [params, setValue]);

  const selectedVehicleId = watch("vehicleId");
  const selectedRentalDurationTier = watch("rentalDurationTier");
  const selectedVehicle = useMemo(
    () => getVehicleById(selectedVehicleId),
    [selectedVehicleId],
  );
  const selectedVehicleTiers = useMemo(
    () => getRentalPriceTiers(selectedVehicle),
    [selectedVehicle],
  );
  const selectedDailyPrice = selectedVehicleTiers
    ? selectedVehicleTiers[selectedRentalDurationTier]
    : null;

  const onSubmit = async (values: QuickBookingFormData) => {
    setIsSubmitting(true);
    setStatus("idle");
    setErrorMessage(null);
    try {
      const nameParts = splitName(values.name);
      const vehicleLabel = getVehicleLabelById(values.vehicleId);
      const selectedVehicle = getVehicleById(values.vehicleId);
      const rentalTiers = getRentalPriceTiers(selectedVehicle);
      const rentalDailyPrice = rentalTiers
        ? rentalTiers[values.rentalDurationTier]
        : null;
      const source = window.location.href;
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attribution: getAttribution(),
          firstName: nameParts.firstName,
          lastName: nameParts.lastName,
          name: values.name,
          email: values.email,
          phone: values.phone,
          subject: `${ui.subjectPrefix}${vehicleLabel || "Sans modele"}`,
          formType: "renseignements",
          quoteRequested: true,
          locationType: "location_vehicule",
          vehicle: values.vehicleId,
          vehicleId: values.vehicleId,
          rentalDurationTier: values.rentalDurationTier,
          rentalDailyPrice: rentalDailyPrice ?? undefined,
          date: values.date || undefined,
          sourcePage: source,
          locale,
          message: values.message,
          honeypot: values.honeypot || "",
        }),
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          typeof responseData?.error === "string"
            ? responseData.error
            : ui.error,
        );
      }
      setStatus("success");
      reset({
        name: "",
        email: "",
        phone: "",
        vehicleId: values.vehicleId,
        rentalDurationTier: values.rentalDurationTier,
        date: "",
        message: "",
      });
    } catch (error: unknown) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : ui.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id={LOCATION_BOOKING_FORM_ID}
      className="scroll-mt-28 bg-white py-12 md:py-14"
    >
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <div className="rounded-2xl border border-primaryLoc/20 bg-white p-5 md:p-7">
          <p className="text-xs uppercase tracking-[0.24em] text-primaryLoc/70">
            {ui.eyebrow}
          </p>
          <h2 className="mt-2 font-space-age text-lg tracking-[0.2em] text-primaryLoc">
            {ui.heading}
          </h2>
          <p className="mt-2 text-sm text-primaryLoc/80">{ui.intro}</p>
          {selectedVehicleId && (
            <div className="mt-3 rounded-lg border border-primaryLoc/20 bg-secondary px-3 py-2 text-sm text-primaryLoc">
              {ui.selectedModel}{" "}
              <span className="font-semibold">
                {getVehicleLabelById(selectedVehicleId)}
              </span>
              {selectedVehicleTiers && (
                <div className="mt-3 grid gap-2 text-xs text-primaryLoc/85 md:grid-cols-3">
                  {RENTAL_DURATION_OPTIONS.map((option) => {
                    const isSelected =
                      option.value === selectedRentalDurationTier;
                    return (
                      <div
                        key={option.value}
                        className={`rounded-md border px-2 py-1.5 ${
                          isSelected
                            ? "border-primaryLoc/50 bg-white"
                            : "border-primaryLoc/15 bg-secondary/35"
                        }`}
                      >
                        <p className="text-[10px] uppercase tracking-[0.02em] text-primaryLoc/70">
                          {durationLabels[option.value]}
                        </p>
                        <p className="font-semibold text-primaryLoc">
                          {formatEuro(selectedVehicleTiers[option.value])}
                          {ui.perDay}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
              {selectedDailyPrice && (
                <p className="mt-2 text-xs font-medium text-primaryLoc">
                  {ui.priceForDuration} {formatEuro(selectedDailyPrice)}
                  {ui.perDay}
                </p>
              )}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <Link
              href={vehiclesBasePath(locale)}
              className="inline-flex min-h-11 items-center rounded-lg border border-primaryLoc/25 px-3 text-primaryLoc transition hover:border-primaryLoc"
            >
              {ui.seeCatalogue}
            </Link>
            {selectedVehicleId && (
              <Link
                href={`${vehiclesBasePath(locale)}/${selectedVehicleId}`}
                className="inline-flex min-h-11 items-center rounded-lg border border-primaryLoc/25 px-3 text-primaryLoc transition hover:border-primaryLoc"
              >
                {ui.seeModel}
              </Link>
            )}
          </div>

          <form
            className="mt-6 grid gap-4 md:grid-cols-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <label className="block">
              <span className="mb-1 block text-sm text-primaryLoc/85">
                {ui.fullName}
              </span>
              <input
                className="h-11 w-full rounded-lg border border-primaryLoc/20 bg-white px-3 text-sm outline-none focus:border-primaryLoc"
                {...register("name", { required: ui.v.nameReq })}
              />
              {errors.name && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.name.message}
                </span>
              )}
            </label>

            <label className="block">
              <span className="mb-1 block text-sm text-primaryLoc/85">
                {ui.email}
              </span>
              <input
                type="email"
                className="h-11 w-full rounded-lg border border-primaryLoc/20 bg-white px-3 text-sm text-primaryLoc outline-none placeholder:text-primaryLoc/45 focus:border-primaryLoc"
                {...register("email", { required: ui.v.emailReq })}
              />
              {errors.email && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.email.message}
                </span>
              )}
            </label>

            <label className="block">
              <span className="mb-1 block text-sm text-primaryLoc/85">
                {ui.phone}
              </span>
              <input
                type="tel"
                className="h-11 w-full rounded-lg border border-primaryLoc/20 bg-white px-3 text-sm text-primaryLoc outline-none placeholder:text-primaryLoc/45 focus:border-primaryLoc"
                placeholder={ui.phonePlaceholder}
                {...register("phone", {
                  required: ui.v.phoneReq,
                  pattern: {
                    value: ui.phonePattern,
                    message: ui.v.phoneInvalid,
                  },
                })}
              />
              {errors.phone && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.phone.message}
                </span>
              )}
            </label>

            <label className="block">
              <span className="mb-1 block text-sm text-primaryLoc/85">
                {ui.vehicle}
              </span>
              <select
                className="h-11 w-full rounded-lg border border-primaryLoc/20 bg-white px-3 text-sm text-primaryLoc outline-none focus:border-primaryLoc"
                {...register("vehicleId", { required: ui.v.vehicleReq })}
              >
                <option value="" className="text-primaryLoc/65">
                  {ui.selectModel}
                </option>
                {vehicleOptions.map((option) => (
                  <option
                    key={option.id}
                    value={option.id}
                    className="text-primaryLoc"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.vehicleId && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.vehicleId.message}
                </span>
              )}
            </label>

            <label className="block">
              <span className="mb-1 block text-sm text-primaryLoc/85">
                {ui.duration}
              </span>
              <select
                className="h-11 w-full rounded-lg border border-primaryLoc/20 bg-white px-3 text-sm text-primaryLoc outline-none focus:border-primaryLoc"
                {...register("rentalDurationTier", {
                  required: ui.v.durationReq,
                })}
              >
                {RENTAL_DURATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {durationLabels[option.value]}
                  </option>
                ))}
              </select>
              {errors.rentalDurationTier && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.rentalDurationTier.message}
                </span>
              )}
            </label>

            <label className="block">
              <span className="mb-1 block text-sm text-primaryLoc/85">
                {ui.date}
              </span>
              <input
                type="date"
                className="h-11 w-full rounded-lg border border-primaryLoc/20 bg-white px-3 text-sm text-primaryLoc outline-none focus:border-primaryLoc"
                {...register("date")}
              />
            </label>

            <label className="block md:col-span-2">
              <span className="mb-1 block text-sm text-primaryLoc/85">
                {ui.message}
              </span>
              <textarea
                rows={4}
                className="w-full rounded-lg border border-primaryLoc/20 bg-white px-3 py-2 text-sm text-primaryLoc outline-none placeholder:text-primaryLoc/45 focus:border-primaryLoc"
                placeholder={ui.messagePlaceholder}
                {...register("message", { required: ui.v.messageReq })}
              />
              {errors.message && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.message.message}
                </span>
              )}
            </label>

            <input
              type="text"
              className="hidden"
              tabIndex={-1}
              {...register("honeypot")}
            />

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex min-h-11 items-center rounded-xl bg-primaryLoc px-5 text-sm font-semibold text-white transition hover:bg-primaryLoc/90 disabled:opacity-60"
              >
                {isSubmitting ? ui.sending : ui.submit}
              </button>
              {status === "success" && (
                <p className="mt-2 text-sm text-primaryLoc">{ui.success}</p>
              )}
              {status === "error" && (
                <p className="mt-2 text-sm text-red-700">
                  {errorMessage || ui.error}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
